/**
 * Merkzettel System
 * Mit API Sync fuer eingeloggte Kunden und Gaeste.
 */

class Merkzettel {
  constructor() {
    this.storageKey = 'ersatz_merkzettel';
    this.guestIdKey = 'cinebuch_guest_id';
    this.apiUrl = 'https://api.cinebuch.de/api/wishlist';
    this.items = this.ladeArtikel();
    this.init();
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    this.aktualisiereAnzahl();
    this.bindeEvents();
    this.markiereAktiveButtons();

    // Bestehende lokale Merkzettel nach Deploy auch ohne neuen Klick synchronisieren.
    if (this.items.length > 0) {
      this.syncToAPI();
    }
  }

  bindeEvents() {
    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-merkzettel-btn]');
      if (!button) return;

      event.preventDefault();
      this.toggle(button);
    });
  }

  ladeArtikel() {
    try {
      const data = localStorage.getItem(this.storageKey);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      return [];
    }
  }

  leseGuestId() {
    try {
      const guestId = localStorage.getItem(this.guestIdKey);
      return guestId || null;
    } catch (error) {
      return null;
    }
  }

  holeOderErzeugeGuestId() {
    const existingGuestId = this.leseGuestId();
    if (existingGuestId && /^guest_[a-z0-9_-]+$/i.test(existingGuestId)) {
      return existingGuestId;
    }

    const guestId = `guest_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;

    try {
      localStorage.setItem(this.guestIdKey, guestId);
    } catch (error) {
      // localStorage unavailable: use the generated id for this request only.
    }

    return guestId;
  }

  normalisiereEmail(value) {
    const email = String(value || '').trim();
    if (!email || email === 'undefined' || email === 'null') {
      return null;
    }
    return email;
  }

  ermittleSyncKontext() {
    const customerId = window.cinebuchCustomer?.id || window.ShopifyAnalytics?.meta?.page?.customerId;
    const email = window.cinebuchCustomer?.email || window.ShopifyAnalytics?.meta?.page?.customerEmail;

    if (customerId) {
      return {
        type: 'member',
        customerId: String(customerId),
        email: this.normalisiereEmail(email),
        guestId: this.leseGuestId()
      };
    }

    return {
      type: 'guest',
      customerId: this.holeOderErzeugeGuestId(),
      email: null,
      guestId: null
    };
  }

  parsePreis(value) {
    if (typeof value === 'number') {
      return value;
    }

    const raw = String(value || '').trim();
    if (!raw) {
      return 0;
    }

    const normalized = raw
      .replace(/[^\d,.-]/g, '')
      .replace(/\.(?=\d{3}(?:\D|$))/g, '')
      .replace(',', '.');

    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  bauePayload(customerId, email, items) {
    return {
      shopify_customer_id: String(customerId),
      email,
      items: items.map((item) => ({
        shopify_product_id: item.id,
        product_handle: item.handle,
        product_title: item.titel,
        product_price: this.parsePreis(item.preis),
        product_image: item.bild
      }))
    };
  }

  async sendeSync(customerId, email, items) {
    return fetch(`${this.apiUrl}/sync`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(this.bauePayload(customerId, email, items))
    });
  }

  async leereGastMerkzettel(guestId) {
    if (!guestId || !/^guest_/i.test(guestId)) {
      return;
    }

    try {
      await this.sendeSync(guestId, null, []);
      localStorage.removeItem(this.guestIdKey);
    } catch (error) {
      // Ignore cleanup errors. Main member sync already succeeded.
    }
  }

  async syncToAPI() {
    const context = this.ermittleSyncKontext();
    if (!context.customerId) {
      return;
    }

    try {
      const response = await this.sendeSync(context.customerId, context.email, this.items);

      if (!response.ok) {
        console.log('API Sync fehlgeschlagen:', response.status);
        return;
      }

      if (context.type === 'member' && context.guestId && context.guestId !== context.customerId && this.items.length > 0) {
        await this.leereGastMerkzettel(context.guestId);
      }

      console.log(
        context.type === 'guest'
          ? 'Gast-Merkzettel erfolgreich synchronisiert'
          : 'Merkzettel erfolgreich synchronisiert'
      );
    } catch (error) {
      console.log('API Sync uebersprungen (offline)');
    }
  }

  speichereArtikel() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.items));
      this.aktualisiereAnzahl();
      this.markiereAktiveButtons();
      this.syncToAPI();
    } catch (error) {
      if (error.name === 'QuotaExceededError') {
        alert('Merkzettel ist voll. Bitte entfernen Sie einige Artikel.');
        this.items.shift();
        this.speichereArtikel();
      } else {
        console.warn('Merkzettel Speicher Fehler:', error);
      }
    }
  }

  toggle(button) {
    const produktId = button.dataset.produktId;
    const index = this.items.findIndex((item) => item.id === produktId);

    if (index > -1) {
      this.items.splice(index, 1);
      button.classList.remove('active');
      button.setAttribute('aria-pressed', 'false');
      this.zeigeBenachrichtigung('Aus Merkzettel entfernt');
    } else {
      const produkt = {
        id: produktId,
        titel: button.dataset.produktTitel,
        handle: button.dataset.produktHandle,
        preis: button.dataset.produktPreis,
        bild: button.dataset.produktBild
      };

      this.items.push(produkt);
      button.classList.add('active');
      button.setAttribute('aria-pressed', 'true');
      this.zeigeBenachrichtigung('Zum Merkzettel hinzugefuegt');
    }

    this.speichereArtikel();
  }

  markiereAktiveButtons() {
    document.querySelectorAll('[data-merkzettel-btn]').forEach((button) => {
      const produktId = button.dataset.produktId;
      const isInWishlist = this.items.some((item) => item.id === produktId);

      if (isInWishlist) {
        button.classList.add('active');
        button.setAttribute('aria-pressed', 'true');
      } else {
        button.classList.remove('active');
        button.setAttribute('aria-pressed', 'false');
      }
    });
  }

  aktualisiereAnzahl() {
    const counter = document.getElementById('wishlist-count');
    if (!counter) {
      return;
    }

    const anzahl = this.items.length;
    counter.textContent = anzahl;
    counter.classList.toggle('opacity-0', anzahl === 0);
  }

  zeigeBenachrichtigung(nachricht) {
    if (window.themeNotifications) {
      window.themeNotifications.show(nachricht);
    }
  }
}

if (typeof window !== 'undefined') {
  window.merkzettel = new Merkzettel();
}
